import React, { useState, useEffect } from 'react';
import { db, collection, addDoc, onSnapshot, updateDoc, doc, handleFirestoreError, OperationType, Timestamp, auth, signInWithEmailAndPassword } from '../firebase';
import { UserProfile } from '../types';
import { useAuth } from '../App';
import { 
  ShieldAlert, 
  Lock, 
  Unlock, 
  Upload, 
  FileText, 
  Search, 
  Key, 
  CheckCircle2, 
  AlertCircle, 
  Loader2, 
  X, 
  UserCheck, 
  Eye,
  Building2,
  FileSpreadsheet,
  Paperclip
} from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';
import { toast } from 'sonner';
import { cn } from '../lib/utils';

export const EpamModule: React.FC = () => {
  const { profile: adminProfile, isAdmin } = useAuth();
  const [users, setUsers] = useState<UserProfile[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterTab, setFilterTab] = useState<'all' | 'locked' | 'pending'>('all');

  // Reset Modal state
  const [selectedUser, setSelectedUser] = useState<UserProfile | null>(null);
  const [isResetModalOpen, setIsResetModalOpen] = useState(false);
  const [letterFile, setLetterFile] = useState<File | null>(null);
  const [letterDataUrl, setLetterDataUrl] = useState<string>('');
  const [adminPassword, setAdminPassword] = useState('');
  const [resetNote, setResetNote] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  // View Letter Modal
  const [viewingLetterUser, setViewingLetterUser] = useState<UserProfile | null>(null);

  useEffect(() => {
    const unsubscribe = onSnapshot(collection(db, 'users'), (snapshot) => {
      const fetchedUsers = snapshot.docs.map(d => ({ uid: d.id, ...d.data() } as UserProfile));
      setUsers(fetchedUsers);
      setLoading(false);
    }, (error) => {
      handleFirestoreError(error, OperationType.LIST, 'users');
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > 5 * 1024 * 1024) { // 5MB limit
      toast.error('File size exceeds 5MB limit');
      return;
    }

    setLetterFile(file);
    const reader = new FileReader();
    reader.onload = (event) => {
      setLetterDataUrl(event.target?.result as string || '');
    };
    reader.readAsDataURL(file);
  };

  const handleExecuteReset = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!selectedUser) return;

    if (!letterDataUrl || !letterFile) {
      toast.error('Application letter signed by HR is strictly required to reset PIN');
      return;
    }

    if (!adminPassword.trim()) {
      toast.error('Please enter your Admin account password to authorize this action');
      return;
    }

    if (!adminProfile?.email) {
      toast.error('Admin session invalid');
      return;
    }

    setIsSubmitting(true);

    try {
      // Re-authenticate Admin account password with Firebase Auth
      await signInWithEmailAndPassword(auth, adminProfile.email, adminPassword);
    } catch (authErr: any) {
      console.error('Admin password verification failed:', authErr);
      toast.error('Admin Password verification failed. Incorrect password entered.');
      setIsSubmitting(false);
      return;
    }

    try {
      await updateDoc(doc(db, 'users', selectedUser.uid), {
        isPayrollLocked: false,
        failedPinAttempts: 0,
        isPinResetByAdmin: true,
        epamResetLetterUrl: letterDataUrl,
        epamResetLetterName: letterFile.name,
        epamResetNote: resetNote.trim(),
        epamResetAt: Timestamp.now(),
        epamResetBy: adminProfile.displayName || adminProfile.email
      });

      // Send alert notification to the employee's notification bar
      await addDoc(collection(db, 'notifications'), {
        userId: selectedUser.uid,
        title: 'Payroll PIN Reset',
        message: 'Your Employee Payroll Access PIN has been reset by Admin following HR request approval. You can now create a new PIN in your My Payroll section.',
        read: false,
        createdAt: Timestamp.now()
      });

      toast.success(`Payroll PIN for ${selectedUser.displayName || selectedUser.email} reset successfully! Notification sent to employee.`);
      setIsResetModalOpen(false);
      setSelectedUser(null);
      setLetterFile(null);
      setLetterDataUrl('');
      setAdminPassword('');
      setResetNote('');
    } catch (error) {
      handleFirestoreError(error, OperationType.UPDATE, `users/${selectedUser.uid}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isAdmin) {
    return (
      <div className="p-8 text-center bg-white rounded-3xl border border-slate-100 font-bold text-slate-500">
        Access Denied: EPAM module is restricted to Administrators only.
      </div>
    );
  }

  const lockedUsersCount = users.filter(u => u.isPayrollLocked).length;
  const resetPendingCount = users.filter(u => u.isPinResetByAdmin).length;

  const filteredUsers = users.filter(u => {
    if (filterTab === 'locked' && !u.isPayrollLocked) return false;
    if (filterTab === 'pending' && !u.isPinResetByAdmin) return false;

    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      const name = (u.displayName || '').toLowerCase();
      const email = (u.email || '').toLowerCase();
      const dept = (u.department || '').toLowerCase();
      const role = (u.role || '').toLowerCase();
      return name.includes(term) || email.includes(term) || dept.includes(term) || role.includes(term);
    }

    return true;
  });

  return (
    <div className="space-y-6">
      {/* Header Banner */}
      <div className="bg-gradient-to-r from-blue-900 via-indigo-900 to-slate-900 text-white rounded-[2.5rem] p-6 sm:p-8 shadow-xl relative overflow-hidden">
        <div className="absolute top-0 right-0 p-8 opacity-10 pointer-events-none">
          <ShieldAlert className="w-64 h-64 text-white" />
        </div>
        <div className="relative z-10 space-y-4 max-w-2xl">
          <div className="flex items-center gap-3">
            <div className="px-3 py-1 bg-blue-500/20 backdrop-blur-md rounded-full border border-blue-400/30 text-blue-200 text-xs font-black uppercase tracking-widest flex items-center gap-1.5">
              <ShieldAlert className="w-3.5 h-3.5 text-blue-300" /> EPAM Module
            </div>
            {lockedUsersCount > 0 && (
              <span className="px-3 py-1 bg-red-500/30 text-red-200 border border-red-500/40 rounded-full text-xs font-black animate-pulse">
                {lockedUsersCount} Account(s) Blocked
              </span>
            )}
          </div>
          <h2 className="text-2xl sm:text-3xl font-black tracking-tight">
            Employee Payroll Access Management (EPAM)
          </h2>
          <p className="text-slate-300 text-sm font-medium leading-relaxed">
            Manage and reset Employee Payroll Access PINs securely. Resetting a locked account strictly requires an uploaded application letter signed by HR and Admin password verification.
          </p>
        </div>
      </div>

      {/* Metrics Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="bg-white p-6 rounded-3xl border border-slate-100 shadow-sm flex items-center gap-4">
          <div className="w-12 h-12 rounded-2xl bg-slate-100 text-slate-800 flex items-center justify-center shrink-0">
            <UserCheck className="w-6 h-6" />
          </div>
          <div>
            <div className="text-2xl font-black text-slate-900">{users.length}</div>
            <div className="text-xs font-bold text-slate-500">Total System Users</div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-3xl border border-red-100 shadow-sm flex items-center gap-4">
          <div className="w-12 h-12 rounded-2xl bg-red-50 text-red-600 flex items-center justify-center shrink-0">
            <Lock className="w-6 h-6" />
          </div>
          <div>
            <div className="text-2xl font-black text-red-600">{lockedUsersCount}</div>
            <div className="text-xs font-bold text-slate-500">Locked / Blocked Accounts</div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-3xl border border-blue-100 shadow-sm flex items-center gap-4">
          <div className="w-12 h-12 rounded-2xl bg-blue-50 text-blue-600 flex items-center justify-center shrink-0">
            <Key className="w-6 h-6" />
          </div>
          <div>
            <div className="text-2xl font-black text-blue-600">{resetPendingCount}</div>
            <div className="text-xs font-bold text-slate-500">Reset Pending (Awaiting PIN)</div>
          </div>
        </div>
      </div>

      {/* Filter & Search Controls */}
      <div className="bg-white p-4 rounded-3xl border border-slate-100 shadow-sm flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div className="flex bg-slate-100 p-1.5 rounded-2xl gap-1">
          <button
            onClick={() => setFilterTab('all')}
            className={cn(
              "px-5 py-2 rounded-xl text-xs font-bold transition-all cursor-pointer",
              filterTab === 'all' ? "bg-white text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-900"
            )}
          >
            All Users ({users.length})
          </button>
          <button
            onClick={() => setFilterTab('locked')}
            className={cn(
              "px-5 py-2 rounded-xl text-xs font-bold transition-all cursor-pointer flex items-center gap-1.5",
              filterTab === 'locked' ? "bg-red-500 text-white shadow-sm" : "text-slate-500 hover:text-slate-900"
            )}
          >
            <Lock className="w-3.5 h-3.5" /> Blocked ({lockedUsersCount})
          </button>
          <button
            onClick={() => setFilterTab('pending')}
            className={cn(
              "px-5 py-2 rounded-xl text-xs font-bold transition-all cursor-pointer flex items-center gap-1.5",
              filterTab === 'pending' ? "bg-blue-600 text-white shadow-sm" : "text-slate-500 hover:text-slate-900"
            )}
          >
            <Key className="w-3.5 h-3.5" /> Reset Pending ({resetPendingCount})
          </button>
        </div>

        <div className="relative w-full md:w-80">
          <Search className="w-4 h-4 absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search by name, email, department..."
            className="w-full pl-10 pr-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-xs font-medium focus:outline-none focus:ring-2 focus:ring-blue-800/10"
          />
        </div>
      </div>

      {/* Users Table */}
      {loading ? (
        <div className="flex justify-center p-12 bg-white rounded-3xl border border-slate-100">
          <Loader2 className="w-8 h-8 animate-spin text-blue-900" />
        </div>
      ) : filteredUsers.length === 0 ? (
        <div className="p-12 text-center bg-white rounded-3xl border border-slate-100 space-y-2">
          <AlertCircle className="w-10 h-10 text-slate-300 mx-auto" />
          <h4 className="font-bold text-slate-700 text-base">No Users Found</h4>
          <p className="text-xs text-slate-400">There are no user accounts matching your filter criteria.</p>
        </div>
      ) : (
        <div className="bg-white rounded-3xl border border-slate-100 shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-100 text-[10px] uppercase font-black tracking-widest text-slate-400">
                  <th className="p-4">User Details</th>
                  <th className="p-4">Department & Role</th>
                  <th className="p-4">Payroll Access Status</th>
                  <th className="p-4">Failed PIN Attempts</th>
                  <th className="p-4">HR Application Letter</th>
                  <th className="p-4 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 text-xs font-medium">
                {filteredUsers.map((u) => {
                  const isLocked = Boolean(u.isPayrollLocked);
                  const isPending = Boolean(u.isPinResetByAdmin);
                  const failedAttempts = u.failedPinAttempts || 0;

                  return (
                    <tr key={u.uid} className={isLocked ? 'bg-red-50/30' : 'hover:bg-slate-50/50'}>
                      <td className="p-4">
                        <div className="flex items-center gap-3">
                          <div className={cn(
                            "w-10 h-10 rounded-2xl flex items-center justify-center font-black text-sm",
                            isLocked ? "bg-red-100 text-red-700" : isPending ? "bg-blue-100 text-blue-700" : "bg-slate-100 text-slate-700"
                          )}>
                            {(u.displayName || u.email || 'U').substring(0, 2).toUpperCase()}
                          </div>
                          <div>
                            <div className="font-bold text-slate-900 text-sm">{u.displayName || 'Unnamed User'}</div>
                            <div className="text-slate-400 text-xs">{u.email}</div>
                          </div>
                        </div>
                      </td>

                      <td className="p-4">
                        <div className="font-bold text-slate-800">{u.department || 'General'}</div>
                        <div className="text-[10px] uppercase tracking-wider font-bold text-slate-400">{u.role}</div>
                      </td>

                      <td className="p-4">
                        {isLocked ? (
                          <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-red-100 text-red-800 font-black text-[11px] uppercase tracking-wider">
                            <Lock className="w-3.5 h-3.5 text-red-600" /> Blocked / Locked
                          </span>
                        ) : isPending ? (
                          <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-blue-100 text-blue-800 font-black text-[11px] uppercase tracking-wider">
                            <Key className="w-3.5 h-3.5 text-blue-600" /> Reset Pending
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-emerald-100 text-emerald-800 font-black text-[11px] uppercase tracking-wider">
                            <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" /> Active
                          </span>
                        )}
                      </td>

                      <td className="p-4">
                        <div className="flex items-center gap-1.5">
                          <span className={cn(
                            "font-bold text-xs",
                            failedAttempts >= 5 ? "text-red-600 font-black" : failedAttempts > 0 ? "text-amber-600" : "text-slate-500"
                          )}>
                            {failedAttempts} / 5
                          </span>
                          {failedAttempts > 0 && failedAttempts < 5 && (
                            <span className="text-[10px] text-amber-600 bg-amber-50 px-2 py-0.5 rounded-md font-bold">
                              Warning
                            </span>
                          )}
                        </div>
                      </td>

                      <td className="p-4">
                        {u.epamResetLetterUrl ? (
                          <button
                            onClick={() => setViewingLetterUser(u)}
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-slate-100 hover:bg-slate-200 text-slate-800 rounded-xl font-bold text-xs transition-all cursor-pointer"
                          >
                            <FileText className="w-3.5 h-3.5 text-blue-600" /> View Letter
                          </button>
                        ) : (
                          <span className="text-slate-300 italic text-xs">None Attached</span>
                        )}
                      </td>

                      <td className="p-4 text-right">
                        <button
                          onClick={() => {
                            setSelectedUser(u);
                            setLetterFile(null);
                            setLetterDataUrl('');
                            setAdminPassword('');
                            setResetNote('');
                            setIsResetModalOpen(true);
                          }}
                          className={cn(
                            "px-4 py-2 rounded-xl font-bold text-xs transition-all cursor-pointer inline-flex items-center gap-1.5 shadow-sm",
                            isLocked 
                              ? "bg-red-600 hover:bg-red-700 text-white shadow-red-200" 
                              : "bg-slate-900 hover:bg-slate-800 text-white"
                          )}
                        >
                          <Unlock className="w-3.5 h-3.5" />
                          {isLocked ? 'Reset Locked PIN' : 'Reset PIN (EPAM)'}
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* EPAM Reset PIN Modal */}
      <AnimatePresence>
        {isResetModalOpen && selectedUser && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => {
                if (!isSubmitting) {
                  setIsResetModalOpen(false);
                  setSelectedUser(null);
                }
              }}
              className="absolute inset-0 bg-slate-900/50 backdrop-blur-sm"
            />
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              className="relative w-full max-w-lg bg-white rounded-[2.5rem] shadow-2xl overflow-hidden my-8 max-h-[90vh] flex flex-col"
            >
              <div className="p-6 bg-slate-900 text-white flex items-center justify-between shrink-0">
                <div className="flex items-center gap-3">
                  <div className="p-2.5 bg-blue-500/20 rounded-xl text-blue-300">
                    <ShieldAlert className="w-6 h-6" />
                  </div>
                  <div>
                    <h3 className="font-black text-lg">EPAM - Reset Payroll PIN</h3>
                    <p className="text-xs text-slate-300">Authorize Payroll PIN Reset for User</p>
                  </div>
                </div>
                <button
                  onClick={() => {
                    setIsResetModalOpen(false);
                    setSelectedUser(null);
                  }}
                  disabled={isSubmitting}
                  className="p-1.5 hover:bg-white/10 rounded-xl transition-colors"
                >
                  <X className="w-5 h-5 text-slate-300" />
                </button>
              </div>

              <form onSubmit={handleExecuteReset} className="p-6 overflow-y-auto space-y-5 flex-1 text-sm">
                {/* Target User Info Summary */}
                <div className="p-4 bg-slate-50 border border-slate-200 rounded-2xl space-y-2">
                  <div className="text-[10px] uppercase font-black tracking-wider text-slate-400">Target Employee</div>
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="font-bold text-slate-900 text-base">{selectedUser.displayName || 'Unnamed User'}</div>
                      <div className="text-xs text-slate-500">{selectedUser.email}</div>
                    </div>
                    {selectedUser.isPayrollLocked ? (
                      <span className="px-3 py-1 bg-red-100 text-red-800 rounded-full font-black text-xs uppercase tracking-wider">
                        Locked (5/5)
                      </span>
                    ) : (
                      <span className="px-3 py-1 bg-slate-200 text-slate-700 rounded-full font-bold text-xs">
                        Active
                      </span>
                    )}
                  </div>
                </div>

                {/* Requirement 1: HR Application Letter Upload */}
                <div className="space-y-2">
                  <label className="block text-xs font-black uppercase tracking-wider text-slate-700 flex items-center justify-between">
                    <span>1. Upload HR Signed Application Letter *</span>
                    <span className="text-red-600 font-bold text-[10px]">Strictly Mandatory</span>
                  </label>
                  <p className="text-xs text-slate-500">
                    Admin cannot reset a user's payroll PIN without an official application letter signed by HR attached.
                  </p>

                  <div className="relative border-2 border-dashed border-slate-200 hover:border-blue-500 bg-slate-50 rounded-2xl p-4 text-center transition-all group cursor-pointer">
                    <input
                      type="file"
                      required
                      accept=".pdf,.png,.jpg,.jpeg"
                      onChange={handleFileChange}
                      className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                    />
                    {letterFile ? (
                      <div className="flex items-center justify-between p-2 bg-emerald-50 border border-emerald-200 rounded-xl text-left">
                        <div className="flex items-center gap-3 overflow-hidden">
                          <FileText className="w-6 h-6 text-emerald-600 shrink-0" />
                          <div className="truncate">
                            <div className="font-bold text-emerald-900 text-xs truncate">{letterFile.name}</div>
                            <div className="text-[10px] text-emerald-700">{(letterFile.size / 1024).toFixed(1)} KB - HR Letter Attached</div>
                          </div>
                        </div>
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            setLetterFile(null);
                            setLetterDataUrl('');
                          }}
                          className="p-1 hover:bg-emerald-100 rounded-lg text-emerald-700"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    ) : (
                      <div className="flex flex-col items-center justify-center gap-2 py-2">
                        <Upload className="w-6 h-6 text-blue-600 group-hover:scale-110 transition-transform" />
                        <div>
                          <p className="font-bold text-slate-800 text-xs">Click to select HR Signed Application Letter</p>
                          <p className="text-[10px] text-slate-400">PDF, PNG, JPG (Max 5MB)</p>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Requirement 2: Admin Password Verification */}
                <div className="space-y-2">
                  <label className="block text-xs font-black uppercase tracking-wider text-slate-700">
                    2. Admin User Account Password *
                  </label>
                  <p className="text-xs text-slate-500">
                    Enter your Admin password to re-authenticate and confirm this EPAM action.
                  </p>
                  <input
                    type="password"
                    required
                    value={adminPassword}
                    onChange={(e) => setAdminPassword(e.target.value)}
                    placeholder="Enter your admin account password..."
                    className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl font-bold focus:ring-2 focus:ring-blue-800/10 focus:outline-none"
                  />
                </div>

                {/* Optional Admin Notes */}
                <div className="space-y-1">
                  <label className="block text-xs font-bold text-slate-600">
                    Admin Reference / Notes (Optional)
                  </label>
                  <input
                    type="text"
                    value={resetNote}
                    onChange={(e) => setResetNote(e.target.value)}
                    placeholder="e.g. HR Letter Ref #1042 - Approved by HR Dept"
                    className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl font-medium focus:outline-none text-xs"
                  />
                </div>

                {/* Submit Action */}
                <div className="pt-2 flex gap-3">
                  <button
                    type="button"
                    onClick={() => {
                      setIsResetModalOpen(false);
                      setSelectedUser(null);
                    }}
                    disabled={isSubmitting}
                    className="flex-1 py-3 bg-slate-100 text-slate-700 font-bold rounded-xl hover:bg-slate-200 transition-all text-xs"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={isSubmitting || !letterFile || !adminPassword.trim()}
                    className="flex-1 py-3 bg-blue-900 text-white font-bold rounded-xl hover:bg-blue-800 transition-all shadow-lg shadow-blue-900/20 disabled:opacity-50 disabled:cursor-not-allowed text-xs flex items-center justify-center gap-2"
                  >
                    {isSubmitting ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" />
                        Verifying & Resetting...
                      </>
                    ) : (
                      <>
                        <Unlock className="w-4 h-4" />
                        Reset Payroll PIN
                      </>
                    )}
                  </button>
                </div>
              </form>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* View HR Letter Modal */}
      <AnimatePresence>
        {viewingLetterUser && viewingLetterUser.epamResetLetterUrl && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setViewingLetterUser(null)}
              className="absolute inset-0 bg-black/60 backdrop-blur-sm"
            />
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="relative w-full max-w-2xl bg-white rounded-[2.5rem] shadow-2xl overflow-hidden max-h-[90vh] flex flex-col"
            >
              <div className="p-6 bg-slate-900 text-white flex items-center justify-between shrink-0">
                <div className="flex items-center gap-3">
                  <FileText className="w-6 h-6 text-blue-400" />
                  <div>
                    <h3 className="font-black text-lg">HR Signed Application Letter</h3>
                    <p className="text-xs text-slate-300">User: {viewingLetterUser.displayName || viewingLetterUser.email}</p>
                  </div>
                </div>
                <button
                  onClick={() => setViewingLetterUser(null)}
                  className="p-1.5 hover:bg-white/10 rounded-xl text-slate-300"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="p-6 overflow-y-auto space-y-4 flex-1">
                <div className="text-xs text-slate-500 flex justify-between bg-slate-50 p-3 rounded-xl border border-slate-200">
                  <span>File: <strong className="text-slate-800">{viewingLetterUser.epamResetLetterName || 'HR_Application_Letter'}</strong></span>
                  <span>Reset By: <strong className="text-slate-800">{viewingLetterUser.epamResetBy || 'Admin'}</strong></span>
                </div>

                {viewingLetterUser.epamResetLetterUrl.startsWith('data:image') ? (
                  <img 
                    src={viewingLetterUser.epamResetLetterUrl} 
                    alt="HR Application Letter" 
                    className="w-full h-auto rounded-2xl border border-slate-200 object-contain max-h-[60vh]"
                  />
                ) : viewingLetterUser.epamResetLetterUrl.startsWith('data:application/pdf') ? (
                  <iframe 
                    src={viewingLetterUser.epamResetLetterUrl} 
                    title="PDF HR Letter" 
                    className="w-full h-[60vh] rounded-2xl border border-slate-200"
                  />
                ) : (
                  <div className="p-8 text-center bg-slate-50 rounded-2xl">
                    <Paperclip className="w-10 h-10 text-slate-400 mx-auto mb-2" />
                    <a 
                      href={viewingLetterUser.epamResetLetterUrl} 
                      download={viewingLetterUser.epamResetLetterName || "HR_Application_Letter"}
                      className="px-4 py-2 bg-blue-900 text-white rounded-xl font-bold text-xs inline-block"
                    >
                      Download HR Letter File
                    </a>
                  </div>
                )}
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
};
